import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { ArrowLeftIcon } from '@heroicons/react/24/outline';
import { apiClient } from '../utils/api';

interface ContentItem {
  id: string;
  title: string;
  description: string;
  contentType: 'movie' | 'series' | 'documentary' | 'live' | 'video' | 'episode';
  mediaType: 'hls' | 'dash' | 'mp4' | 'video';
  mediaUrl?: string;
  mediaFilePath?: string;
  thumbnailUrl?: string;
  durationSeconds: number;
  totalEpisodes?: number;
  episodeNumber?: number;
  groupName?: string;
  thirdPartyId?: string;
  broadcastUrls?: string[];
  tags?: string[];
  status: string;
  createdAt: string;
  updatedAt: string;
}

interface ContentForm {
  title: string;
  description: string;
  contentType: 'movie' | 'series' | 'documentary' | 'live' | 'video' | 'episode';
  mediaUrl: string;
  thumbnailUrl: string;
  durationSeconds: number;
  totalEpisodes?: number;
  episodeNumber?: number;
  groupName: string;
  thirdPartyId: string;
  broadcastUrls: string[];
  tags: string[];
}

const ContentEdit: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [content, setContent] = useState<ContentItem | null>(null);
  const [form, setForm] = useState<ContentForm>({
    title: '',
    description: '',
    contentType: 'video',
    mediaUrl: '',
    thumbnailUrl: '',
    durationSeconds: 0,
    totalEpisodes: 1,
    episodeNumber: 1,
    groupName: '',
    thirdPartyId: '',
    broadcastUrls: [],
    tags: []
  });
  
  const [tagInput, setTagInput] = useState('');
  const [broadcastUrlInput, setBroadcastUrlInput] = useState('');
  const [loading, setLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  useEffect(() => {
    if (id) {
      fetchContent();
    }
  }, [id]);

  const fetchContent = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await apiClient.get<{data: ContentItem}>(`/content/${id}`);
      
      if (response.data) {
        const contentData = response.data;
        setContent(contentData);
        
        // Populate form with existing data
        setForm({
          title: contentData.title,
          description: contentData.description || '',
          contentType: contentData.contentType,
          mediaUrl: contentData.mediaUrl || '',
          thumbnailUrl: contentData.thumbnailUrl || '',
          durationSeconds: contentData.durationSeconds,
          totalEpisodes: contentData.totalEpisodes || 1,
          episodeNumber: contentData.episodeNumber || 1,
          groupName: contentData.groupName || '',
          thirdPartyId: contentData.thirdPartyId || '',
          broadcastUrls: contentData.broadcastUrls || [],
          tags: contentData.tags || []
        });
      } else {
        setError('İçerik bulunamadı');
      }
    } catch (error) {
      console.error('Error fetching content:', error);
      setError('İçerik yüklenirken hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setForm(prev => ({
      ...prev,
      [name]: name === 'durationSeconds' || name === 'totalEpisodes' || name === 'episodeNumber' 
        ? parseInt(value) || 0 
        : value
    }));
  };

  const handleAddTag = () => {
    if (tagInput.trim() && !form.tags.includes(tagInput.trim())) {
      setForm(prev => ({
        ...prev,
        tags: [...prev.tags, tagInput.trim()]
      }));
      setTagInput('');
    }
  };

  const handleRemoveTag = (tagToRemove: string) => {
    setForm(prev => ({
      ...prev,
      tags: prev.tags.filter(tag => tag !== tagToRemove)
    }));
  };

  const handleAddBroadcastUrl = () => {
    if (broadcastUrlInput.trim() && !form.broadcastUrls.includes(broadcastUrlInput.trim())) {
      try {
        new URL(broadcastUrlInput.trim()); // Validate URL
        setForm(prev => ({
          ...prev,
          broadcastUrls: [...prev.broadcastUrls, broadcastUrlInput.trim()]
        }));
        setBroadcastUrlInput('');
      } catch {
        alert('Geçerli bir URL girin');
      }
    }
  };

  const handleRemoveBroadcastUrl = (urlToRemove: string) => {
    setForm(prev => ({
      ...prev,
      broadcastUrls: prev.broadcastUrls.filter(url => url !== urlToRemove)
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!content) return;

    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      // Prepare update data - only include fields that can be updated
      const updateData = {
        title: form.title,
        description: form.description,
        durationSeconds: form.durationSeconds,
        thumbnailUrl: form.thumbnailUrl || undefined,
        tags: form.tags.length > 0 ? form.tags : undefined,
        // Include episode info for series
        ...(form.contentType === 'series' && {
          totalEpisodes: form.totalEpisodes,
          episodeNumber: form.episodeNumber
        }),
        groupName: form.groupName,
        thirdPartyId: form.thirdPartyId,
        broadcastUrls: form.broadcastUrls.length > 0 ? form.broadcastUrls : undefined
      };

      console.log('Updating content with data:', updateData);

      await apiClient.put(`/content/${content.id}`, updateData);
      
      setSubmitStatus('success');
      
      // Navigate back to content detail after a short delay
      setTimeout(() => {
        navigate(`/content/${content.id}`);
      }, 1500);
    } catch (error) {
      console.error('Error updating content:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const getTypeText = (type: string): string => {
    switch (type) {
      case 'movie':
        return 'Film';
      case 'series':
        return 'Dizi';
      case 'episode':
        return 'Bölüm';
      case 'documentary':
        return 'Belgesel';
      case 'live':
        return 'Canlı';
      case 'video':
        return 'Video';
      default:
        return type;
    }
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 rounded w-1/4 mb-4"></div>
          <div className="bg-white rounded-lg shadow p-6">
            <div className="h-6 bg-gray-200 rounded mb-4"></div>
            <div className="space-y-4">
              {[1, 2, 3, 4].map((i) => (
                <div key={i} className="h-10 bg-gray-200 rounded"></div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error || !content) {
    return (
      <div className="text-center py-12">
        <h3 className="mt-2 text-sm font-medium text-gray-900">Hata Oluştu</h3>
        <p className="mt-1 text-sm text-gray-500">{error || 'İçerik bulunamadı'}</p>
        <div className="mt-6">
          <Link
            to="/content"
            className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
          >
            İçerik Listesine Dön
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center space-x-4">
        <Link
          to={`/content/${content.id}`}
          className="inline-flex items-center text-gray-500 hover:text-gray-700"
        >
          <ArrowLeftIcon className="h-5 w-5 mr-1" />
          İçerik Detayı
        </Link>
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-300">İçerik Düzenle</h1>
          <p className="text-sm text-gray-500">{content.title}</p>
        </div>
      </div>

      {/* Form */}
      <div className="bg-white shadow rounded-lg p-6">
        {submitStatus === 'success' && (
          <div className="mb-6 p-4 bg-green-100 border border-green-400 text-green-700 rounded">
            ✅ İçerik başarıyla güncellendi!
          </div>
        )}

        {submitStatus === 'error' && (
          <div className="mb-6 p-4 bg-red-100 border border-red-400 text-red-700 rounded">
            ❌ İçerik güncellenirken bir hata oluştu. Lütfen tekrar deneyin.
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Title */}
          <div>
            <label htmlFor="title" className="block text-sm font-medium text-gray-700 mb-2">
              Başlık *
            </label>
            <input
              type="text"
              id="title"
              name="title"
              value={form.title}
              onChange={handleInputChange}
              required
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white text-gray-900"
              placeholder="İçerik başlığını girin"
            />
          </div>

          {/* Description */}
          <div>
            <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-2">
              Açıklama
            </label>
            <textarea
              id="description"
              name="description"
              value={form.description}
              onChange={handleInputChange}
              rows={4}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white text-gray-900"
              placeholder="İçerik açıklamasını girin"
            />
          </div>

          {/* Content Type (Read-only) */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              İçerik Türü
            </label>
            <div className="px-3 py-2 bg-gray-100 border border-gray-300 rounded-md text-gray-600">
              {getTypeText(form.contentType)}
              <span className="text-xs text-gray-500 ml-2">(Değiştirilemez)</span>
            </div>
          </div>

          {/* Media URL (Read-only if file-based) */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Medya Kaynağı
            </label>
            {content.mediaFilePath ? (
              <div className="px-3 py-2 bg-gray-100 border border-gray-300 rounded-md text-gray-600">
                Dosya: {content.mediaFilePath}
                <span className="text-xs text-gray-500 ml-2">(Yüklenen dosya)</span>
              </div>
            ) : (
              <input
                type="url"
                name="mediaUrl"
                value={form.mediaUrl}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white text-gray-900"
                placeholder="https://example.com/video.mp4"
              />
            )}
          </div>

          {/* Thumbnail URL */}
          <div>
            <label htmlFor="thumbnailUrl" className="block text-sm font-medium text-gray-700 mb-2">
              Thumbnail URL
            </label>
            <input
              type="url"
              id="thumbnailUrl"
              name="thumbnailUrl"
              value={form.thumbnailUrl}
              onChange={handleInputChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white text-gray-900"
              placeholder="https://example.com/thumbnail.jpg"
            />
          </div>

          {/* Duration and Episode Info */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div>
              <label htmlFor="durationSeconds" className="block text-sm font-medium text-gray-700 mb-2">
                Süre (saniye)
              </label>
              <input
                type="number"
                id="durationSeconds"
                name="durationSeconds"
                value={form.durationSeconds}
                onChange={handleInputChange}
                min="0"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white text-gray-900"
                placeholder="0"
              />
            </div>

            {form.contentType === 'series' && (
              <>
                <div>
                  <label htmlFor="totalEpisodes" className="block text-sm font-medium text-gray-700 mb-2">
                    Toplam Bölüm
                  </label>
                  <input
                    type="number"
                    id="totalEpisodes"
                    name="totalEpisodes"
                    value={form.totalEpisodes}
                    onChange={handleInputChange}
                    min="1"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white text-gray-900"
                    placeholder="1"
                  />
                </div>

                <div>
                  <label htmlFor="episodeNumber" className="block text-sm font-medium text-gray-700 mb-2">
                    Bölüm Numarası
                  </label>
                  <input
                    type="number"
                    id="episodeNumber"
                    name="episodeNumber"
                    value={form.episodeNumber}
                    onChange={handleInputChange}
                    min="1"
                    max={form.totalEpisodes}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="1"
                  />
                </div>
              </>
            )}
          </div>

          {/* Group Name */}
          <div>
            <label htmlFor="groupName" className="block text-sm font-medium text-gray-700 mb-2">
              Grup Adı
            </label>
            <input
              type="text"
              id="groupName"
              name="groupName"
              value={form.groupName}
              onChange={handleInputChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white text-gray-900"
              placeholder="Grup adını girin"
            />
          </div>

          {/* Third Party ID */}
          <div>
            <label htmlFor="thirdPartyId" className="block text-sm font-medium text-gray-700 mb-2">
              Üçüncü Taraf ID
            </label>
            <input
              type="text"
              id="thirdPartyId"
              name="thirdPartyId"
              value={form.thirdPartyId}
              onChange={handleInputChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white text-gray-900"
              placeholder="Üçüncü taraf ID'ni girin"
            />
          </div>

                     {/* Broadcast URLs */}
           <div>
             <label className="block text-sm font-medium text-gray-700 mb-2">
               Yayın URL'leri
             </label>
             <div className="flex gap-2 mb-2">
               <input
                 type="url"
                 value={broadcastUrlInput}
                 onChange={(e) => setBroadcastUrlInput(e.target.value)}
                 onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddBroadcastUrl())}
                 className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white text-gray-900"
                 placeholder="https://broadcast-url.com/stream"
               />
               <button
                 type="button"
                 onClick={handleAddBroadcastUrl}
                 className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-500"
               >
                 Ekle
               </button>
             </div>
             <div className="space-y-2">
               {form.broadcastUrls.map((url, index) => (
                 <div
                   key={index}
                   className="flex items-center justify-between p-2 bg-gray-100 rounded-md"
                 >
                   <span className="text-sm text-gray-700 flex-1 truncate">{url}</span>
                   <button
                     type="button"
                     onClick={() => handleRemoveBroadcastUrl(url)}
                     className="ml-2 text-red-500 hover:text-red-700"
                   >
                     ×
                   </button>
                 </div>
               ))}
             </div>
             <p className="text-xs text-gray-500 mt-1">
               Çoklu yayın kaynakları için URL'ler ekleyebilirsiniz
             </p>
           </div>

          {/* Tags */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Etiketler
            </label>
            <div className="flex gap-2 mb-2">
              <input
                type="text"
                value={tagInput}
                onChange={(e) => setTagInput(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddTag())}
                className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white text-gray-900"
                placeholder="Etiket ekle"
              />
              <button
                type="button"
                onClick={handleAddTag}
                className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                Ekle
              </button>
            </div>
            <div className="flex flex-wrap gap-2">
              {form.tags.map((tag, index) => (
                <span
                  key={index}
                  className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-blue-100 text-blue-800"
                >
                  {tag}
                  <button
                    type="button"
                    onClick={() => handleRemoveTag(tag)}
                    className="ml-2 text-blue-600 hover:text-blue-800"
                  >
                    ×
                  </button>
                </span>
              ))}
            </div>
          </div>

          {/* Submit Buttons */}
          <div className="flex justify-end space-x-3">
            <Link
              to={`/content/${content.id}`}
              className="px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
            >
              İptal
            </Link>
            <button
              type="submit"
              disabled={isSubmitting}
              className="px-6 py-2 bg-blue-600 text-white font-medium rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSubmitting ? 'Güncelleniyor...' : 'Güncelle'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ContentEdit;